// volBars.js — volume-bar chart type (vol-bars-20261101).
// Chart type per chart slot: 'time' (normal) or 'vol' (each candle closes
// after N SOL traded). Persisted in localStorage. Candles are built
// client-side straight from the raw trade buffer (TradeBuffer columns), so
// depth = whatever trades the buffer holds (server backfill + live).
import { newLiveCandle, addTickToLive } from './candleHelpers'

export const VOL_SIZES = [1, 5, 10, 25, 50, 100]   // SOL per bar; 0 = Auto
// cmevol-20261102: CME futures bars close after N CONTRACTS (no SOL/mcap/supply
// there). Presets per root, persisted per root in `cmeSize`; Auto = default.
export const CME_VOL_SIZES = {
  ES: [250, 500, 1000, 2500, 5000],
  MES: [100, 250, 500, 1000, 2500],
  NQ: [100, 250, 500, 1000, 2500],
  CL: [100, 250, 500, 1000, 2500],
  GC: [25, 50, 100, 250, 500],
}
export const CME_VOL_DEFAULT = { ES: 1000, MES: 500, NQ: 500, CL: 500, GC: 100 }
const KEY = (id) => `solcheddar:chartType.${id || 'main'}`
const _subs = new Set()
const _cache = new Map()

export function getVolBars(id) {
  if (_cache.has(id)) return _cache.get(id)
  let v = { type: 'time', size: 0, cmeSize: {} }
  try {
    const raw = JSON.parse(localStorage.getItem(KEY(id)) || 'null')
    if (raw && (raw.type === 'vol' || raw.type === 'time')) v = { type: raw.type, size: Number(raw.size) || 0, cmeSize: (raw.cmeSize && typeof raw.cmeSize === 'object') ? raw.cmeSize : {} }
  } catch {}
  _cache.set(id, v)
  return v
}
export function setVolBars(id, patch) {
  const v = { ...getVolBars(id), ...patch }
  _cache.set(id, v)
  try { localStorage.setItem(KEY(id), JSON.stringify(v)) } catch {}
  _subs.forEach(fn => { try { fn(id, v) } catch {} })
}
export function onVolBarsChange(fn) { _subs.add(fn); return () => _subs.delete(fn) }

// The buffer's amount is the TOKEN amount; trade mcap is in SOL, so the SOL
// notional is amount * mcap / supply. (No supply -> futures: amount * price.)
// vol-fix-20261101: sizes were compared against raw token amounts, so every
// trade alone exceeded 1..100 and every size drew the same one-trade bars.
// supply < 0 = CME: count contracts (cmevol-20261102).
export const solOf = (a, m, supply) => supply < 0 ? a : (supply > 0 ? a * m / supply : a * m)
const NICE = [0.5, 1, 2, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000]
// Auto: ~1 bar per minute of this coin's recent flow (last 30 min in the buffer).
export function autoVolSize(buf, supply = 0) {
  const n = buf?.length || 0
  if (!n) return 5
  const newest = buf.newestTime()
  const from = newest - 30 * 60 * 1000
  let vol = 0, first = newest
  for (let i = n - 1; i >= 0; i--) {
    const t = buf.timeAt(i)
    if (t < from) break
    vol += solOf(buf.amountAt(i), buf.mcapAt(i), supply); first = t
  }
  const mins = Math.max(1, (newest - first) / 60000)
  const target = vol / mins
  let best = NICE[0]
  for (const s of NICE) if (Math.abs(Math.log(s / target)) < Math.abs(Math.log(best / target))) best = s
  return best
}

// Incremental builder. State per chart lives in `st` (a ref object).
// Rebuilds fully when size/bucket changes or the buffer was trimmed/prepended
// or rewritten; otherwise only folds trades appended since the last call.
// Returns a stable array (mutated in place) so the caller's structural check
// sees a new length only when a bar closes.
// vol-hist-20261101: `hist` = older raw trades fetched from the server
// ({t,s,a,m} parallel arrays, chronological, all older than the buffer).
// Folded first, then the live buffer.
function foldTrade(st, t, side, a, m, size, bucket, supply) {
  if (!(a > 0) || !(m > 0)) return
  const arr = st.arr
  let c = st.cur
  if (!c || st.acc >= size) {
    c = newLiveCandle(t, bucket)
    const prev = arr[arr.length - 1]
    if (prev && c.startTime <= prev.startTime) c.startTime = prev.startTime + 1
    c._volBar = true
    arr.push(c); st.cur = c; st.acc = 0
  }
  addTickToLive(c, m, side, a)
  if (c.open_mcap == null) c.open_mcap = m
  c.close_mcap = m
  if (c.high_mcap == null || m > c.high_mcap) c.high_mcap = m
  if (c.low_mcap == null || m < c.low_mcap) c.low_mcap = m
  c._endTime = t
  st.acc += solOf(a, m, supply)
}
// cmevol2-20261102: `bufFrom` (CME only) = exact history/live seam in ms. History
// covers t < bufFrom, the buffer t >= bufFrom, so the seam slot is neither lost
// nor doubled. `hist.gen` forces a rebuild when history is rewritten in place.
export function buildVolCandles(st, buf, size, bucket, supply = 0, hist = null, bufFrom = 0) {
  const n = buf?.length || 0
  const oldest = n ? buf.timeAt(0) : null
  const hLen = hist ? hist.t.length : 0
  const full = !st.arr || st.size !== size || st.bucket !== bucket || st.supply !== supply ||
    st.oldest !== oldest || n < st.n || st.hLen !== hLen || st.hGen !== (hist?.gen || 0) || st.bufFrom !== bufFrom
  if (full) {
    st.arr = []; st.cur = null; st.acc = 0; st.n = 0
    st.size = size; st.bucket = bucket; st.supply = supply; st.oldest = oldest; st.hLen = hLen; st.hGen = hist?.gen || 0; st.bufFrom = bufFrom
    const lim = n ? Math.max(oldest, bufFrom) : Infinity
    for (let i = 0; i < hLen; i++) {
      if (hist.t[i] >= lim) break
      foldTrade(st, hist.t[i], hist.s[i], hist.a[i], hist.m[i], size, bucket, supply)
    }
  }
  const arr = st.arr
  for (let i = st.n; i < n; i++) {
    const t = buf.timeAt(i), m = buf.mcapAt(i), a = buf.amountAt(i)
    if (!(a > 0) || !(m > 0)) continue
    if (t < bufFrom) continue
    let c = st.cur
    if (!c || st.acc >= size) {
      c = newLiveCandle(t, bucket)
      // startTime must be unique & increasing (keys, dedupe): nudge ties.
      const prev = arr[arr.length - 1]
      if (prev && c.startTime <= prev.startTime) c.startTime = prev.startTime + 1
      c._volBar = true
      arr.push(c); st.cur = c; st.acc = 0
    }
    addTickToLive(c, m, buf.sideAt(i), a)
    if (c.open_mcap == null) c.open_mcap = m
    c.close_mcap = m
    if (c.high_mcap == null || m > c.high_mcap) c.high_mcap = m
    if (c.low_mcap == null || m < c.low_mcap) c.low_mcap = m
    c._endTime = t
    st.acc += solOf(a, m, supply)
  }
  st.n = n
  return st.arr
}
